// src/render/bus.ts — render-layer event bus.
//
// Distinct from the sim's GameEventMap: UI overlays (DevOverlay) subscribe to
// render-frame events (tick progress, FPS) without the sim needing to know
// anything about the render loop. Plan 04 wires GameScene -> renderBus.emit;
// Plan 04 also wires DevOverlay -> renderBus.on.
//
// P2 (Plan 02-03) extensions:
//   - Sim events forwarded by GameScene.update() after each tick():
//     entity-moved, torch-flicked, motion-light-on, motion-light-off.
//     These are one-way sim→render per D-17 / PATTERNS.md Pattern G. Type shapes
//     mirror the corresponding sim-side GameEventMap variants so DevOverlay +
//     future P6 audio can subscribe to either bus and get the same payload.
//   - lighting-debug: synthetic per-sim-tick snapshot emitted by GameScene.update()
//     carrying { torchOn, fovTileCount, motionLights[], tick } so DevOverlay can
//     render the locked Torch + FOV + Lights rows (UI-SPEC §DevOverlay P2
//     extension) without reading sim state directly.

import { EventBus } from '../shared/bus';
import type { Vec2 } from '../shared/types/intent';
import type {
  EntityRole,
  EntityStatus,
  RoundState,
  BlackboardEntry,
} from '../shared/types/world';
import type { EventTag } from '../shared/types/events';

/**
 * Motion light classification for the DevOverlay P2 ring renderer.
 *
 *   armed   — freshly activated (this tick or just-past) OR entity still inside
 *             the triggerRadius; rendered as soft-green ring (#5cb874).
 *   staying — activated in the past, still within the P2_MOTION_LIGHT_STAY_TICKS
 *             window (4 s), no entity currently re-arming; rendered as amber.
 *   fading  — sim has set fadingTick; in the P2_MOTION_LIGHT_FADE_TICKS window
 *             (12 ticks / 400 ms); rendered as coral.
 *
 * See UI-SPEC §Light Mask Palette + DevOverlay P2 extension rows for the exact
 * hex values. The render-side bus carries the discriminator string; DevOverlay
 * owns the hex lookup.
 */
export type MotionLightDebugState = 'armed' | 'staying' | 'fading';

export interface MotionLightDebugEntry {
  id: string;
  state: MotionLightDebugState;
  activatedTick: number;
}

/**
 * Player position snapshot for the DevOverlay Pos row (UAT Gap 2 closure, 02-07).
 *
 *   world — entity.position in pixels (Tiled world units). Derived from
 *           currState.entities['e0'].position inside GameScene.emitLightingDebug.
 *   tile  — floor(world / tileWidth). Null when cachedMapData is absent (boot
 *           before BootScene finishes) so DevOverlay renders "—" for the tile
 *           coords in that narrow window instead of reading 0,0 as if valid.
 *
 * Kept DEV-oriented: renderBus payloads travel to no production consumer (the
 * only subscriber is DevOverlay which is tree-shaken in prod builds).
 */
export interface PlayerPositionDebug {
  world: Vec2;
  tile: Vec2 | null;
}

export type RenderEventMap = {
  /**
   * Emitted by GameScene.update() once per sim tick. The P3 (Plan 03-08)
   * extension carries everything the HUD needs to drive per-frame state
   * (RoundTimer, HeadstartCountdown, StaminaBar, OnFloorDropButton, etc.)
   * without subscribing directly to sim-tier state.
   *
   * WARNING-15 tier-isolation contract: `playerAbilities` +
   * `characterDisplayName` are resolved from PROFILES at emit time inside
   * GameScene (the render-tier orchestrator). UI components subscribe ONLY
   * to this payload and must NOT import from src/sim/characters/profiles.
   * Admin-CMS loading in P5 (CHAR-07) swaps the resolution source without
   * changing this payload shape.
   */
  'sim-tick': {
    tick: number;
    simTimeMs: number;
    seed: number;
    round: RoundState;
    playerStamina: number;
    playerRole: EntityRole;
    playerStatus: EntityStatus;
    characterId: string;
    /** WARNING-15: the player character's ability tokens resolved at emit
     *  time. Drives visibility of ability-gated HUD controls (e.g.
     *  OnFloorDropButton). Empty array is a valid state — means no abilities. */
    playerAbilities: string[];
    /** WARNING-15: capitalised display name resolved at emit time for the
     *  StaminaBar tag. P5 admin CMS swaps the resolution source without
     *  changing the payload shape. */
    characterDisplayName: string;
    /** P6 (Plan 06-05) — local player world position (in pixels) cached by
     *  the audio footsteps module for AUDIO-08 distance-cull math. May be
     *  null in the brief boot window before the player entity is spawned. */
    playerPosition: Vec2 | null;
    /** P6 (Plan 06-05) — current map tile width in pixels for tile↔pixel
     *  unit conversion in the footstep distance-cull. Defaults to 32 when
     *  the cached map data is absent. */
    mapTileWidth: number;
    /** P6 (Plan 06-12, UAT Gap 4): per-entity status snapshot for render-side
     *  D-11 parity guards (e.g. BubbleLayer hidden-hider speech silence —
     *  the fear-threshold-crossed admit handler short-circuits when the
     *  speaker entity's status is 'hiding' or 'caught'). Optional `?` for
     *  backward compat with existing test fixtures that construct partial
     *  sim-tick payloads. Populated by the GameScene bridge from
     *  currState.entities at emit time — sim purity preserved (the sim does
     *  not need to know about this render-side derivation). */
    entityStatuses?: Record<string, EntityStatus>;
    /** P7 (Plan 07-07, UI-05 / D-08): the free hider the spectator camera is
     *  currently following, or null when the local player is not spectating.
     *  `name` is the watched hider's display name, resolved render-side at emit
     *  time exactly like `characterDisplayName` (WARNING-15 tier isolation — the
     *  SpectatorBanner reads it off this payload and never imports
     *  src/sim/characters). Optional `?` for backward compat with existing
     *  partial sim-tick test fixtures. */
    spectatorWatching?: { id: string; name: string } | null;
  };
  'render-fps': { fps: number };
  // Phase 7 (Plan 07-07, D-02): in-game pause contract. The PauseButton (Plan 09)
  // and App (Plan 10) emit `pause-requested` / `resume-requested`; GameScene
  // consumes them and drives the SAME ticker-pause mechanism the existing
  // MOVE-08 visibilitychange handler uses (game.loop.sleep/wake). There is NO
  // second pause loop (Pitfall D) — `source` only records who asked so a future
  // readout can distinguish auto-pause (tab backgrounded) from an explicit menu
  // pause.
  'pause-requested': { source: 'visibility' | 'menu' };
  'resume-requested': { source: 'visibility' | 'menu' };
  // Phase 1:
  'smoke-test': { ok: boolean; pathLength: number; from: string; to: string };
  'zone-labels': { zones: Array<{ id: string; center: { x: number; y: number } }> };
  // Phase 2 (Plan 02-03) — sim events forwarded by GameScene.update().
  // Phase 6 (Plan 06-05) extension: per-entity context fields needed by the
  // render-tier audio footsteps module (status / characterId / sprint /
  // position). Additive — emitter side enriches the payload from the live
  // sim state at forward time; sim shape unchanged so the golden hash stays
  // byte-stable on 2dfba10c. status drives D-11 silent-hide short-circuit;
  // characterId drives the per-character quietness lookup; sprint drives
  // P6_FOOTSTEP_SPRINT_GAIN; position is a denormalised mirror of `to` for
  // distance-cull math without re-reading sim state.
  'entity-moved': {
    entityId: string;
    from: Vec2;
    to: Vec2;
    tick: number;
    /** P6 (Plan 06-05) — denormalised position (mirrors `to`). */
    position: Vec2;
    /** P6 (Plan 06-05) — character profile id for quietness lookup. */
    characterId: string;
    /** P6 (Plan 06-05) — D-11 silent-hide short-circuit. */
    status: EntityStatus;
    /** P6 (Plan 06-05) — drives P6_FOOTSTEP_SPRINT_GAIN volume bump. */
    sprint: boolean;
  };
  'torch-flicked': { entityId: string; on: boolean; tick: number };
  'motion-light-on': { lightId: string; tick: number };
  'motion-light-off': { lightId: string; tick: number };
  // Phase 2 (Plan 02-03, extended Plan 02-07 Gap 2 closure) — synthetic
  // per-sim-tick snapshot for DevOverlay. `playerPosition` was added to close
  // UAT Gap 2 (in-game coordinates for playtesting on the real Mouchao map).
  'lighting-debug': {
    torchOn: boolean;
    fovTileCount: number;
    motionLights: MotionLightDebugEntry[];
    tick: number;
    playerPosition: PlayerPositionDebug;
  };
  // Phase 3 (Plan 03-07) — 12 sim events forwarded by GameScene.update()
  // (shapes identical to src/shared/types/events.ts GameEventMap):
  'round-started': { roundNumber: number; seed: number; tick: number };
  'round-ended': {
    roundNumber: number;
    endReason: 'all-caught' | 'timer';
    caughtCount: number;
    totalHiders: number;
    tick: number;
  };
  'team-switched': { roundNumber: number; tick: number };
  'catch': { hunterId: string; hiderId: string; tick: number };
  'hider-parked': { hunterId: string; hiderId: string; tick: number };
  'hider-released': { liberatorId: string; freedIds: string[]; tick: number };
  'foxguard-called': { hunterId: string; shouterId: string; tick: number };
  'hiding-entered': { entityId: string; spotId: string; tick: number };
  'hiding-exited': { entityId: string; spotId: string; tick: number };
  'peek-started': { entityId: string; tick: number };
  'peek-ended': { entityId: string; tick: number };
  'stamina-depleted': { entityId: string; tick: number };
  // Phase 3 (Plan 03-07) — render-only derived event emitted by GameScene
  // when WorldState.derived.hideCandidate changes between ticks. HUD (Plan 08)
  // subscribes to drive the context-button armed/disarmed state without
  // re-querying the sim per frame.
  'hide-candidate-changed': {
    entityId: string;
    spotId: string | null;
    tick: number;
  };
  // Phase 4 (Plan 04-04, D-11) — forwarded from sim 'awareness-changed'.
  // GameScene subscribes internally to drive per-hunter telegraph sprites;
  // DevOverlay + P6 audio can also subscribe without reading sim state.
  'awareness-changed': {
    entityId: string;
    level: 'unaware' | 'suspicious' | 'alerted' | 'spotted';
    tick: number;
  };
  // Phase 4 (Plan 04-10, Task 3) — 5 new P4 sim events forwarded by GameScene.
  // Shapes mirror the corresponding GameEventMap variants (events.ts).
  'shout': { sourceId: string; position: Vec2; tick: number };
  'fear-threshold-crossed': {
    entityId: string;
    threshold: 'nervous' | 'afraid' | 'terrified';
    direction: 'up' | 'down';
    tick: number;
  };
  'dog-barked': { dogId: string; position: Vec2; tick: number };
  'adult-commented': {
    adultId: string;
    eventTag: EventTag;
    lineIndex: number;
    tick: number;
  };
  // Phase 4 (Plan 04-10, Task 3) — synthetic per-sim-tick AI debug snapshot.
  // DevOverlay subscribes to render blackboard visualiser + fear readout.
  // Gated DEV-only on the emit side (GameScene); payload passes through unchanged.
  // Phase 11 (Plan 11-04, D-01): npcBranches extended with per-NPC AI state
  // (branch / torch / awareness / checkedSpots) for the DevOverlay NPC panel.
  // Optional for backward compatibility with existing callers that emit without it.
  'ai-debug': {
    blackboardHunter: BlackboardEntry[];
    blackboardHider: BlackboardEntry[];
    fears: Record<string, number>;
    tick: number;
    npcBranches?: Record<string, {
      branch: string;       // agent.winningBranch
      torch: boolean;       // entity.torchOn from WorldState
      awareness: string;    // 'unaware' | 'suspicious' | 'alerted' | 'spotted'
      checkedSpots: number; // agent.checkedHidingSpotIds.size (hunters only)
    }>;
  };
  // Phase 4 (Plan 04-09, D-37) — sim proposes initial roster before round 1;
  // RoundSetup.tsx subscribes to seed its drag-drop state.
  // Plan 10-05 (WR-04): playerCharacterId is included so RoundSetup can lock
  // the player's own chip to the hider column (non-draggable), preventing the
  // CR-01 corrupted-roster path from being reachable via normal drag gestures.
  'round-setup-propose': {
    hunters: string[];
    hiders: string[];
    benched: string[];
    playerCharacterId?: string;
  };
  // Phase 4 (Plan 04-09, D-37) — RoundSetup emits confirmed roster when player
  // clicks "Confirm"; sim tier (Plan 10) subscribes to apply overrides.
  'round-setup-confirmed': {
    hunters: string[];
    hiders: string[];
    benched: string[];
  };
  // ===== Phase 6 (D-04, D-23) — Charm Layer =====
  // 'character-spoke' is the ONE load-bearing variant. The shape mirrors the
  // GameEventMap variant exactly — GameScene.update() does
  //   for (const ev of simEvents) if (ev.type === 'character-spoke') renderBus.emit('character-spoke', ev);
  // (the same P3 sim→render bridge pattern used for catch/free-all/etc).
  //
  // The remaining four are Claude's-discretion DEV-only telemetry hooks
  // (CONTEXT D-23). They tree-shake from prod via import.meta.env.DEV
  // gating at the emitter site (06-03 audio manager + 06-04 BubbleLayer).
  'character-spoke': {
    entityId: string;
    characterId: string;
    eventTag: EventTag;
    lineIndex: number;
    tick: number;
  };
  /** DEV-only: live concurrent Howl source count for the audio debug overlay. */
  'audio-debug': { liveSources: number; tick: number };
  /** DEV-only: a bubble was admitted to the BubbleLayer cap-3 queue. */
  'bubble-shown': { entityId: string; tier: string; tick: number };
  /** DEV-only: a footstep SFX fired (post throttle + distance cull). */
  'footstep': { entityId: string; tick: number };
  /** DEV-only: a stinger SFX fired by name (registry key in stinger-registry.ts). */
  'stinger': { name: string; tick: number };
};

/** Singleton render bus — one per client; GameScene emits, UI overlays subscribe. */
export const renderBus = new EventBus<RenderEventMap>();
